'use client';

import * as React from 'react';
import { EventBus } from '@/shared/utils';
import { LearningPaths as LearningPathsDict } from '@/shared/i18n';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { LearningPathByIdEventKeys, LearningPathByIdEvents } from '../events';
import { useLearningPathById } from '../hooks/use-learning-path-by-id';
import { Typography } from '@mui/material';
import LessonList from '../ui/components/lesson-list';
import { SummaryDetails } from '../ui/components/summary-details';
import Link from 'next/link';
import { VideoLessonDialog } from '../ui/components/dialogs/video-lesson';
import { LessonCompletionDialog } from '../ui/components/dialogs/lesson-completion';
import { Auth } from 'firebase/auth';
import NavigationButton from 'src/components/layout/navigation-button';

type LearningPathByIdScreenProps = {
  auth: Auth | null;
  eventBus: EventBus<LearningPathByIdEvents>;
  dict: LearningPathsDict;
  learningPathDocumentId: string;
  learningPathName: string;
  accessToken?: string;
};

export default function LearningPathByIdScreen({
  eventBus,
  dict,
  learningPathDocumentId,
  learningPathName,
  auth,
  accessToken,
}: LearningPathByIdScreenProps) {
  const {
    customerSubscription,
    fetchCustomerSubscription,
    fetchLearningPathByIdSummary,
    fetchLearningPathByIdLessons,
    createLessonProgresses,
    updateLessonProgresses,
    fetchFavourite,
    createFavourite,
    deleteFavourite,
    resetStore,
  } = useLearningPathById(auth, eventBus, accessToken);

  const isSubscriber = !!customerSubscription?.data?.subscriber;

  React.useEffect(() => {
    eventBus.on(LearningPathByIdEventKeys.customer.view, fetchCustomerSubscription);
    eventBus.on(LearningPathByIdEventKeys.summary.view, fetchLearningPathByIdSummary);
    eventBus.on(LearningPathByIdEventKeys.lessons.view, fetchLearningPathByIdLessons);
    eventBus.on(LearningPathByIdEventKeys.lessonProgresses.create, createLessonProgresses);
    eventBus.on(LearningPathByIdEventKeys.lessonProgresses.update, updateLessonProgresses);
    eventBus.on(LearningPathByIdEventKeys.favourites.view, fetchFavourite);
    eventBus.on(LearningPathByIdEventKeys.favourites.create, createFavourite);
    eventBus.on(LearningPathByIdEventKeys.favourites.delete, deleteFavourite);

    return () => {
      eventBus.off(LearningPathByIdEventKeys.customer.view, fetchCustomerSubscription);
      eventBus.off(LearningPathByIdEventKeys.summary.view, fetchLearningPathByIdSummary);
      eventBus.off(LearningPathByIdEventKeys.lessons.view, fetchLearningPathByIdLessons);
      eventBus.off(LearningPathByIdEventKeys.lessonProgresses.create, createLessonProgresses);
      eventBus.off(LearningPathByIdEventKeys.lessonProgresses.update, updateLessonProgresses);
      eventBus.off(LearningPathByIdEventKeys.favourites.view, fetchFavourite);
      eventBus.off(LearningPathByIdEventKeys.favourites.create, createFavourite);
      eventBus.off(LearningPathByIdEventKeys.favourites.delete, deleteFavourite);
      resetStore();
    };
  }, [eventBus, learningPathDocumentId]);

  return (
    <div>
      <NavigationButton
        label="Indietro"
        onClick={() => eventBus.emit(LearningPathByIdEventKeys.redirects.back, undefined)}
      />
      <div>
        <SummaryDetails
          learningPathDocumentId={learningPathDocumentId}
          eventBus={eventBus}
          isSubscriber={isSubscriber}
        />
      </div>
      <div className="max-w-[800px] m-auto">
        <div className="my-12">
          <LessonList
            learningPathDocumentId={learningPathDocumentId}
            learningPathName={learningPathName}
            eventBus={eventBus}
            isSubscriber={isSubscriber}
          />
        </div>
      </div>
      <VideoLessonDialog
        eventBus={eventBus}
        auth={auth}
        learningPathDocumentId={learningPathDocumentId}
      />
      <LessonCompletionDialog
        eventBus={eventBus}
        learningPathDocumentId={learningPathDocumentId}
        learningPathName={learningPathName}
      />
    </div>
  );
}
